/* ═══════════════════════════════════════════════════════════════
   Flaschenlage: Wo steht die Flasche im Bild?

   Die erste Fassung des Rundum-Etiketts nahm an, dass die Flasche
   genau im eingeblendeten Umriss steht und die Kamera stillhält. Ein
   echtes Video (Flasche auf einem Deckel, von Hand gedreht, Telefon
   frei gehalten) zeigte, wie wenig das trägt: Die Achse wanderte um
   fast einen halben Flaschenradius, die Oberkante um 16 Pixel, die
   Kamera kam im Lauf 4 % näher, und die Flasche sah anders aus als
   die Bordeaux-Vorlage (längerer Hals, tiefere Schulter). Jede
   dieser Abweichungen verschiebt das Messband — und damit die Haut.

   Deshalb wird die Lage in jedem Bild gemessen:

   - **Achse** (mit Neigung) aus Kantenpaaren: In jeder Zeile von Hals
     und Bauch wird links und rechts im erwarteten Abstand die
     kräftigste Kante gesucht. Nur Paare, deren halber Abstand zum
     Umriss passt, zählen; ihre Mittelpunkte ergeben robust eine
     Gerade. Einseitige Kanten — ein Etikett, das gerade am Rand
     vorbeidreht, die Struktur einer Rauputzwand — finden so kein
     Gegenstück und fallen heraus.
   - **Oberkante**: die oberste kräftige waagerechte Kante entlang der
     Achse (Kapsel gegen Hintergrund).
   - **Maßstab** aus der Schulter: Ihr Umriss ist drehunabhängig, steil
     und fast immer Glas vor Hintergrund. Die Zeile, in der die
     Flasche halb so breit ist wie zwischen Hals und Bauch, ergibt
     zusammen mit der Oberkante den senkrechten Maßstab. Gegenprobe
     am Video: 1,043 gegenüber 1,046 laut Etiketthöhe — der Radius aus
     den Bauchkanten kam nur auf 1,030, weil die Kanten am Bauch je
     nach Etikett springen.

   Der **Umriss** selbst wird vor der Aufnahme gemessen, solange die
   Vorschau läuft (mit festem Maßstab — die Vorlage hat ihre Schulter
   woanders): je Bild und Zeile die äußerste kräftige Kante links und
   rechts, gesammelt als Häufigkeit je Höhe und Seite; je Seite der
   Median, und weichen die Seiten ab, die weitere (eine falsche Kante
   liegt innen — am Video stand die rechte Seite im Schatten der
   Flasche). Hals und Bauch werden danach durch robuste Geraden
   ersetzt — einzelne Ausreißer an Etikettkanten verschwinden, und die
   Verjüngung nach unten (die Kamera schaut von oben) bleibt erhalten.
   Am Hals zählt auch die Buntheit: Eine goldene Kapsel vor einer
   beigen Wand hat kaum Helligkeitskanten.

   Einheiten: Ein Bild ist { g, breite, hoehe } (Helligkeit als
   Float32Array). Höhen entlang der Flasche heißen u und zählen in
   Einheiten des senkrechten Maßstabs S ab der Oberkante:
   y = oben + u · S. Radien heißen ρ und zählen in Einheiten des
   Bauchradius R: r = ρ · R. Die Lage ist
   { cx, neig, R, oben, S } mit der Achse x(y) = cx + neig · (y − yBezug),
   yBezug = oben + 4 · S.

   Reine Logik ohne DOM; Tests in tests/flaschenlage.test.mjs.
   ═══════════════════════════════════════════════════════════════ */

import { formVon, koerperRadius, radiusBei } from "./flasche.js";


const LUMA = [0.299, 0.587, 0.114];

/** RGBA (ImageData-artig) → Helligkeit. Die Farben bleiben daneben
    erhalten (`rgba`): Die Suche nach Umriss und Oberkante zieht auch
    die Buntheit heran — eine goldene Kapsel vor einer beigen Wand ist
    kaum heller oder dunkler als die Wand, aber deutlich bunter. */
function luminanz(bild) {
  const { data, width, height } = bild;
  const g = new Float32Array(width * height);
  for (let i = 0, p = 0; i < g.length; i++, p += 4) {
    g[i] = data[p] * LUMA[0] + data[p + 1] * LUMA[1] + data[p + 2] * LUMA[2];
  }
  return { g, rgba: data, breite: width, hoehe: height };
}

/* Buntheit eines Pixels: größter minus kleinster Farbwert */
const bunt = (d, o) => {
  const r = d[o * 4], g = d[o * 4 + 1], b = d[o * 4 + 2];
  return Math.max(r, g, b) - Math.min(r, g, b);
};

const yBezug = (lage) => lage.oben + 4 * lage.S;
const achseX = (lage, y) => lage.cx + lage.neig * (y - yBezug(lage));


/* ── Profile ──────────────────────────────────────────────────── */

const DU = 0.02;

/** Profil aus einer Grundform (Vorlage, solange nichts gemessen ist). */
function profilAusForm(formSchluessel) {
  const form = formVon(formSchluessel);
  const Rk = koerperRadius(form);
  const uBoden = form.hoehe / Rk;
  const n = Math.ceil(uBoden / DU) + 1;
  const rho = new Float32Array(n);
  for (let i = 0; i < n; i++) rho[i] = radiusBei(form, form.hoehe - i * DU * Rk) / Rk;
  return { ...profilZonen({ du: DU, rho, uBoden, gemessen: false, form: formSchluessel }), c: 1 };
}

const rhoBei = (profil, u) => {
  const p = u / profil.du;
  if (!(p >= 0)) return profil.rho[0];
  const i = Math.floor(p);
  if (i >= profil.rho.length - 1) return profil.rho[profil.rho.length - 1];
  const f = p - i;
  return profil.rho[i] * (1 - f) + profil.rho[i + 1] * f;
};


/* Zonen eines Profils: Hals (unter dem Mündungswulst bis vor die
   Schulter), Bauch (ab voller Breite bis kurz vor den Boden) und der
   Schulterpunkt (halbe Breite zwischen Hals und Bauch). Nur Hals und
   Bauch sind annähernd senkrecht — dort sind waagerechte Kanten
   verlässlich und das Messband sauber. */
function profilZonen(profil) {
  const { rho, du, uBoden } = profil;
  const n = rho.length;
  const halsWerte = [];
  for (let i = Math.round(0.25 / du); i < Math.min(n, Math.round(1.5 / du)); i++) halsWerte.push(rho[i]);
  halsWerte.sort((a, b) => a - b);
  const rHals = halsWerte[Math.floor(halsWerte.length / 2)];
  let rBauch = 0;
  for (let i = Math.round(2 / du); i < Math.min(n, Math.round((uBoden - 0.6) / du)); i++) rBauch = Math.max(rBauch, rho[i]);
  const mitte = (rHals + rBauch) / 2;
  /* Schulter: von oben die erste Stelle, an der die Mitte überschritten wird. */
  let schulterU = null;
  for (let i = Math.round(0.6 / du); i < n - 1; i++) {
    if (rho[i] < mitte && rho[i + 1] >= mitte) {
      schulterU = (i + (mitte - rho[i]) / (rho[i + 1] - rho[i])) * du;
      break;
    }
  }
  if (schulterU == null) schulterU = 2.5;
  let halsEnde = schulterU;
  for (let i = Math.round(schulterU / du); i > 0; i--) {
    if (rho[i] <= rHals * 1.08) { halsEnde = i * du; break; }
  }
  let bauchStart = schulterU;
  for (let i = Math.round(schulterU / du); i < n; i++) {
    if (rho[i] >= 0.93 * rBauch) { bauchStart = i * du; break; }
  }
  return {
    ...profil, rHals, rBauch, schulterU, schulterRho: mitte,
    hals: [0.25, Math.max(0.5, halsEnde - 0.1)],
    bauch: [bauchStart + 0.15, uBoden - 0.6],
  };
}


/* ── Kanten ───────────────────────────────────────────────────── */

/* Waagerechter Gradient (über 3 Zeilen geglättet) an der Stelle x.
   `mitFarbe`: der Helligkeit oder, wenn kräftiger, der Buntheit — nur
   für den äußeren Umriss. Beim Verfolgen nicht: Dort brächte die
   Farbe nur zusätzliche Kanten (rote Schrift, Halsschleife), an denen
   die Achse hängen bleibt. */
function gradX(bild, x, y, mitFarbe = false) {
  const { g, rgba, breite, hoehe } = bild;
  const xi = Math.round(x);
  if (xi < 2 || xi > breite - 3 || y < 1 || y > hoehe - 2) return 0;
  let s = 0, f = 0;
  const farbe = mitFarbe && rgba;
  for (let d = -1; d <= 1; d++) {
    const o = (y + d) * breite + xi;
    s += g[o + 2] + g[o + 1] - g[o - 1] - g[o - 2];
    if (farbe) f += bunt(rgba, o + 2) + bunt(rgba, o + 1) - bunt(rgba, o - 1) - bunt(rgba, o - 2);
  }
  return Math.max(Math.abs(s), 0.8 * Math.abs(f)) / 6;
}

/* Kante in [x − w, x + w], auf Bruchteile genau: unter den deutlichen
   örtlichen Maxima (mindestens 45 % des stärksten) das nächste zur
   erwarteten Stelle x. Nicht einfach das stärkste: Dreht sich ein
   Etikettrand an den Umriss heran, ist er oft kräftiger als der
   Umriss selbst — wer ihm folgt, wandert mit dem Etikett nach innen
   und verliert die Flasche. */
function kanteNahe(bild, x, y, w) {
  const x0 = Math.ceil(x - w), x1 = Math.floor(x + w);
  const v = [];
  let bestV = 0;
  for (let xi = x0; xi <= x1; xi++) {
    const g = gradX(bild, xi, y);
    v.push(g);
    if (g > bestV) bestV = g;
  }
  if (!(bestV > 0)) return null;
  const schwelle = Math.max(10, 0.45 * bestV);
  let best = -1;
  for (let i = 0; i < v.length; i++) {
    if (v[i] < schwelle || (i > 0 && v[i] < v[i - 1]) || (i < v.length - 1 && v[i] < v[i + 1])) continue;
    if (best < 0 || Math.abs(x0 + i - x) < Math.abs(x0 + best - x)) best = i;
  }
  if (best < 0) return null;
  let off = 0;
  if (best > 0 && best < v.length - 1) {
    const l = v[best - 1], m = v[best], r = v[best + 1];
    const d = l - 2 * m + r;
    if (d < 0) off = Math.max(-0.5, Math.min(0.5, 0.5 * (l - r) / d));
  }
  return { x: x0 + best + off, staerke: v[best] };
}

/* Äußerste kräftige Kante: von außen nach innen die erste Stelle über
   der Schwelle, dann bis zum örtlichen Maximum. Die Schwelle richtet
   sich nach dem Hintergrund außerhalb (Rauputz ist rau). */
function aeussereKante(bild, a, y, rAussen, rInnen, seite, mitFarbe = false) {
  const { breite } = bild;
  const xs = [];
  for (let r = rAussen; r >= rInnen; r -= 1) {
    const x = a + seite * r;
    if (x < 3 || x > breite - 4) continue;
    xs.push(x);
  }
  if (xs.length < 6) return null;
  const v = xs.map((x) => gradX(bild, x, y, mitFarbe));
  const anfang = v.slice(0, Math.max(4, Math.round(v.length * 0.12))).sort((p, q) => p - q);
  const grund = anfang[Math.floor(anfang.length / 2)];
  /* gradX liefert an einer Stufe deren volle Höhe; die Schwelle ist
     am echten Video eingestellt (Rauputz links, glatte Wand rechts). */
  const schwelle = Math.max(36, 3 * grund + 16);
  for (let i = 0; i < v.length; i++) {
    if (v[i] >= schwelle) {
      while (i + 1 < v.length && v[i + 1] >= v[i]) i++;
      return Math.abs(xs[i] - a);
    }
  }
  return null;
}


/* ── Robuste Gerade ───────────────────────────────────────────── */

/* `boden`: kleinste angenommene Streuung (in den Einheiten von xs —
   Pixel beim Verfolgen, Hundertstel-Radien beim Umriss). */
function robusteGerade(ys, xs, runden = 4, boden = 0.3) {
  const n = ys.length;
  if (n < 4) return null;
  let gut = new Uint8Array(n).fill(1);
  let a = 0, b = 0, s = 1;
  for (let it = 0; it < runden; it++) {
    let sw = 0, sy = 0, sx = 0, syy = 0, syx = 0;
    for (let i = 0; i < n; i++) {
      if (!gut[i]) continue;
      sw++; sy += ys[i]; sx += xs[i]; syy += ys[i] * ys[i]; syx += ys[i] * xs[i];
    }
    if (sw < 3) return null;
    const det = sw * syy - sy * sy;
    b = det > 1e-9 ? (sw * syx - sy * sx) / det : 0;
    a = (sx - b * sy) / sw;
    const res = [];
    for (let i = 0; i < n; i++) if (gut[i]) res.push(Math.abs(xs[i] - (a + b * ys[i])));
    res.sort((p, q) => p - q);
    s = 1.4826 * res[Math.floor(res.length / 2)] + boden;
    for (let i = 0; i < n; i++) gut[i] = Math.abs(xs[i] - (a + b * ys[i])) < 2.5 * s ? 1 : 0;
  }
  let anteil = 0;
  for (let i = 0; i < n; i++) anteil += gut[i];
  return { a, b, streuung: s, anteil: anteil / n };
}

const median = (liste) => {
  if (!liste.length) return null;
  const s = [...liste].sort((p, q) => p - q);
  return s[Math.floor(s.length / 2)];
};


/* ── Erste Lage ───────────────────────────────────────────────── */

/** Erste Lage aus einer groben Vorgabe (dem eingeblendeten Umriss).
    `vorgabe` = { cx, oben, S } in Bildpunkten. Sucht die Bauchkanten
    weiträumig — die äußersten kräftigen Kanten je Zeile, robust zu
    je einer Geraden —, danach die Oberkante. Liefert null, wenn keine
    Flasche zu erkennen ist. */
function lageFinden(bild, vorgabe, profil) {
  let lage = { cx: vorgabe.cx, neig: 0, R: vorgabe.S * profil.rBauch, oben: vorgabe.oben, S: vorgabe.S };
  const obenFenster = 0.25 * vorgabe.S * profil.uBoden;
  for (let runde = 0; runde < 3; runde++) {
    const weit = runde === 0;
    const ys = [], links = [], rechts = [];
    const [u0, u1] = profil.bauch;
    for (let u = u0; u <= u1; u += 0.06) {
      const y = Math.round(lage.oben + u * lage.S);
      if (y < 2 || y > bild.hoehe - 3) continue;
      const a = achseX(lage, y);
      const r = rhoBei(profil, u) * lage.R;
      const l = aeussereKante(bild, a, y, r * (weit ? 1.45 : 1.15), r * (weit ? 0.5 : 0.8), -1);
      const rr = aeussereKante(bild, a, y, r * (weit ? 1.45 : 1.15), r * (weit ? 0.5 : 0.8), 1);
      if (l == null || rr == null) continue;
      ys.push(y); links.push(a - l); rechts.push(a + rr);
    }
    const gl = robusteGerade(ys, links), gr = robusteGerade(ys, rechts);
    if (!gl || !gr || gl.anteil < 0.4 || gr.anteil < 0.4) return null;
    const yb = yBezug(lage);
    const xl = gl.a + gl.b * yb, xr = gr.a + gr.b * yb;
    const R = (xr - xl) / 2;
    if (!(R > 4)) return null;
    lage = { ...lage, cx: (xl + xr) / 2, neig: (gl.b + gr.b) / 2, R, S: R / profil.rBauch };
    const oben = oberkanteSuchen(bild, lage, profil, runde === 0 ? obenFenster : 0.06 * lage.S * profil.uBoden);
    if (oben != null) lage = { ...lage, oben };
  }
  return lage;
}


/** Wie lageFinden, aber über mehrere Größen: Steht die Flasche näher
    oder ferner als der eingeblendete Umriss vorsieht, findet die Suche
    mit der falschen Größe Etikettkanten statt des Umrisses. Jede Größe
    wird um die Mitte des Umrisses gesucht; es gilt die, deren Lage
    die meisten passenden Kantenpaare hat. */
function lageSuchen(bild, vorgabe, profil) {
  const mitte = vorgabe.oben + 0.5 * profil.uBoden * vorgabe.S;
  let best = null;
  for (const f of [1, 0.85, 1.15, 0.72, 1.3]) {
    const S = vorgabe.S * f;
    const lage = lageFinden(bild, { cx: vorgabe.cx, oben: mitte - 0.5 * profil.uBoden * S, S }, profil);
    if (!lage) continue;
    const { guete, verloren } = lageVerfolgen(bild, lage, profil, { mitSchulter: false });
    if (verloren) continue;
    /* Die eingeblendete Größe hat Vorrang: Passt sie ordentlich, wird
       nicht weitergesucht (Etikett- und Kapselkanten können bei
       anderer Größe zufällig ebenso gut passen). */
    if (f === 1 && guete >= 0.55) return { lage, guete };
    if (!best || guete > best.guete + 0.05) best = { lage, guete };
  }
  return best;
}


/* Oberkante: entlang der Achse (± halber Halsradius) die oberste
   kräftige waagerechte Kante im Fenster. */
function oberkanteSuchen(bild, lage, profil, fenster) {
  const { g, rgba, breite, hoehe } = bild;
  const rn = Math.max(2, 0.5 * profil.rHals * lage.R);
  const y0 = Math.max(3, Math.round(lage.oben - fenster)), y1 = Math.min(hoehe - 4, Math.round(lage.oben + fenster));
  if (!(y1 >= y0)) return null;           /* Fenster außerhalb des Bilds */
  const hell = new Float32Array(y1 - y0 + 5), buntProfil = new Float32Array(y1 - y0 + 5);
  for (let y = y0 - 2; y <= y1 + 2; y++) {
    const a = achseX(lage, y);
    let s = 0, f = 0, n = 0;
    for (let x = Math.round(a - rn); x <= Math.round(a + rn); x++) {
      if (x < 0 || x >= breite) continue;
      s += g[y * breite + x]; if (rgba) f += bunt(rgba, y * breite + x); n++;
    }
    hell[y - y0 + 2] = n ? s / n : 0;
    buntProfil[y - y0 + 2] = n ? f / n : 0;
  }
  const sprung = (p, i) => Math.abs(p[i + 2] + p[i + 1] - p[i - 1] - p[i - 2]) / 2;
  const v = [];
  let max = 0;
  for (let y = y0; y <= y1; y++) {
    const i = y - y0 + 2;
    const d = Math.max(sprung(hell, i), rgba ? 0.8 * sprung(buntProfil, i) : 0);
    v.push(d); max = Math.max(max, d);
  }
  const schwelle = Math.max(10, 0.3 * max);
  for (let i = 1; i < v.length - 1; i++) {
    if (v[i] >= schwelle && v[i] >= v[i - 1] && v[i] >= v[i + 1]) {
      const l = v[i - 1], m = v[i], r = v[i + 1];
      const d = l - 2 * m + r;
      return y0 + i + (d < 0 ? Math.max(-0.5, Math.min(0.5, 0.5 * (l - r) / d)) : 0);
    }
  }
  return null;
}


/* ── Verfolgen ────────────────────────────────────────────────── */

/** Lage im nächsten Bild, ausgehend von der bisherigen. Liefert die
    neue Lage samt `guete` (Anteil brauchbarer Kantenpaare) — oder
    null, wenn die Flasche verloren ist. */
function lageVerfolgen(bild, vorher, profil, { mitSchulter = true, massstabFest = false } = {}) {
  let lage = { ...vorher };
  /* 1. Achse aus Kantenpaaren über Hals und Bauch */
  const ys = [], ms = [], halbe = [];
  const w = Math.max(3, 0.07 * lage.R);
  const zeilen = [];
  for (let u = profil.hals[0]; u <= profil.hals[1]; u += 0.05) zeilen.push(u);
  for (let u = profil.bauch[0]; u <= profil.bauch[1]; u += 0.05) zeilen.push(u);
  for (const u of zeilen) {
    const y = Math.round(lage.oben + u * lage.S);
    if (y < 2 || y > bild.hoehe - 3) continue;
    const a = achseX(lage, y);
    const r = rhoBei(profil, u) * lage.R;
    const l = kanteNahe(bild, a - r, y, w), rr = kanteNahe(bild, a + r, y, w);
    if (!l || !rr || l.staerke < 10 || rr.staerke < 10) continue;
    const h = (rr.x - l.x) / 2;
    if (Math.abs(h - r) > 0.035 * lage.R + 1) continue;
    ys.push(y); ms.push((l.x + rr.x) / 2);
    halbe.push(h / rhoBei(profil, u));
  }
  const gerade = robusteGerade(ys, ms);
  const guete = zeilen.length ? (gerade ? gerade.anteil * ys.length / zeilen.length : 0) : 0;
  if (!gerade || ys.length < 8) return { lage: vorher, guete: 0, verloren: true };
  const yb = yBezug(lage);
  lage.cx = gerade.a + gerade.b * yb;
  /* Neigung ändert sich langsam; nur übernehmen, wenn die Zeilen
     weit genug auseinanderliegen. */
  const spanne = ys[ys.length - 1] - ys[0];
  if (spanne > 2 * lage.S) lage.neig = 0.8 * lage.neig + 0.2 * gerade.b;
  /* 2. Oberkante */
  const oben = oberkanteSuchen(bild, lage, profil, Math.max(6, 0.06 * lage.S));
  if (oben != null) lage.oben = oben;
  /* 3. Maßstab: Schulter, sonst Bauchbreite.
     Der Zielradius an der Schulter kommt aus den Kantenpaaren dieses
     Bilds (geglättet), nicht aus dem Maßstab — sonst schaukeln sich
     beide auf: zu kleiner Maßstab → zu kleiner Radius → Schulter zu
     hoch gefunden → Maßstab noch kleiner. */
  const Rmess = median(halbe);
  if (Rmess) lage.Rkante = lage.Rkante ? 0.8 * lage.Rkante + 0.2 * Rmess : Rmess;
  let S = null;
  if (mitSchulter && profil.gemessen && lage.Rkante) {
    const ys_ = schulterSuchen(bild, lage, profil, lage.Rkante);
    if (ys_ != null) S = (ys_ - lage.oben) / profil.schulterU;
  }
  if (S == null && Rmess) S = Rmess / profil.rBauch;
  if (!massstabFest && S != null && Math.abs(S / lage.S - 1) < 0.08) lage.S = 0.7 * lage.S + 0.3 * S;
  lage.R = lage.S * profil.c;
  return { lage, guete, verloren: false };
}


/* Schulter: Zeilen um den erwarteten Schulterpunkt; je Zeile die
   halbe Breite aus dem stärksten symmetrischen Kantenpaar; die Zeile,
   in der sie den Schulterwert erreicht, robust über eine Gerade durch
   die Nachbarschaft. */
function schulterSuchen(bild, lage, profil, R = lage.R) {
  const ziel = profil.schulterRho * R;
  const y0 = Math.round(lage.oben + (profil.schulterU - 0.45) * lage.S);
  const y1 = Math.round(lage.oben + (profil.schulterU + 0.45) * lage.S);
  const rmin = 0.75 * profil.rHals * R, rmax = 1.1 * profil.rBauch * R;
  const ws = [], ys = [];
  for (let y = Math.max(2, y0); y <= Math.min(bild.hoehe - 3, y1); y++) {
    const a = achseX(lage, y);
    let best = 0, bestR = 0;
    for (let r = rmin; r <= rmax; r += 0.5) {
      const e = Math.min(gradX(bild, a - r, y), gradX(bild, a + r, y));
      if (e > best) { best = e; bestR = r; }
    }
    if (best > 12) { ws.push(bestR); ys.push(y); }
  }
  const nah = [], nahY = [];
  for (let i = 0; i < ws.length; i++) {
    if (Math.abs(ws[i] - ziel) < 0.15 * R) { nah.push(ws[i]); nahY.push(ys[i]); }
  }
  if (nah.length < 4) return null;
  const g = robusteGerade(nah, nahY);
  if (!g) return null;
  return g.a + g.b * ziel;
}


/* ── Umriss messen ────────────────────────────────────────────── */

const RHO_STUFEN = 160;     /* ρ von 0 bis 1,6 in Hundertsteln */

function profilSammler(profil) {
  const n = Math.ceil((profil.uBoden + 0.6) / DU);
  /* links und rechts getrennt gezählt (siehe profilBilden) */
  return { n, zaehler: [new Uint16Array(n * RHO_STUFEN), new Uint16Array(n * RHO_STUFEN)], bilder: 0, form: profil.form, uBoden: profil.uBoden };
}

/** Ein Bild zum Umriss beitragen: je Höhe die äußerste kräftige Kante
    links und rechts, als ρ = Abstand / R. */
function profilSammeln(sammler, bild, lage) {
  for (let i = 0; i < sammler.n; i++) {
    const u = i * DU;
    const y = Math.round(lage.oben + u * lage.S);
    if (y < 2 || y > bild.hoehe - 3) continue;
    const a = achseX(lage, y);
    for (const seite of [-1, 1]) {
      /* Am Hals auch die Buntheit (goldene Kapsel vor heller Wand) —
         weiter unten nicht: Dort stehen bunter Teller und Hand. */
      const r = aeussereKante(bild, a, y, 1.4 * lage.R, 0.1 * lage.R, seite, u < 1.8);
      if (r == null) continue;
      const k = Math.round(r / lage.R * 100);
      if (k >= 0 && k < RHO_STUFEN) sammler.zaehler[seite < 0 ? 0 : 1][i * RHO_STUFEN + k]++;
    }
  }
  sammler.bilder++;
}

/** Umriss aus den gesammelten Kanten. Fehlt etwas (zu wenige Bilder,
    keine Schulter erkennbar), bleibt es bei der Vorlage. */
function profilBilden(sammler, vorlage) {
  const { n, zaehler } = sammler;
  if (sammler.bilder < 5) return vorlage;
  /* Median je Seite. Weichen die Seiten ab, gilt die weitere: Eine
     falsche Kante liegt fast immer innen (Spiegelstreifen im Glas,
     wenn der Umriss vor einer Schattenwand kaum zu sehen ist) — außen
     hält die Schwelle den Hintergrund fern. Am Video war so die
     rechte Seite im Schatten der Flasche 12 Pixel zu schmal. */
  const seitenMedian = (z, i) => {
    let summe = 0;
    for (let k = 0; k < RHO_STUFEN; k++) summe += z[i * RHO_STUFEN + k];
    if (summe < Math.max(2, sammler.bilder * 0.3)) return NaN;
    let acc = 0;
    for (let k = 0; k < RHO_STUFEN; k++) {
      acc += z[i * RHO_STUFEN + k];
      if (acc >= summe / 2) return k / 100;
    }
    return NaN;
  };
  const roh = new Float32Array(n).fill(NaN);
  for (let i = 0; i < n; i++) {
    const l = seitenMedian(zaehler[0], i), r = seitenMedian(zaehler[1], i);
    if (Number.isNaN(l) && Number.isNaN(r)) continue;
    roh[i] = Number.isNaN(l) ? r : Number.isNaN(r) ? l : Math.abs(l - r) < 0.04 ? (l + r) / 2 : Math.max(l, r);
  }
  /* Lücken füllen, Median über 9, Mittel über 5 */
  const gefuellt = lueckenFuellen(roh);
  if (!gefuellt) return vorlage;
  const glatt = mittelGlaetten(medianGlaetten(gefuellt, 9), 5);
  let profil = profilZonen({ du: DU, rho: glatt, uBoden: vorlage.uBoden, gemessen: true, form: vorlage.form });
  /* Hals und Bauch durch robuste Geraden ersetzen */
  const ersetze = ([u0, u1]) => {
    const us = [], rs = [];
    for (let i = Math.round(u0 / DU); i <= Math.round(u1 / DU) && i < n; i++) { us.push(i * DU); rs.push(glatt[i]); }
    const g = robusteGerade(us, rs, 4, 0.004);
    if (!g) return;
    for (let i = Math.round(u0 / DU); i <= Math.round(u1 / DU) && i < n; i++) glatt[i] = g.a + g.b * i * DU;
    /* Bauch bis zum Boden verlängern — unten verdecken oft Teller und Hand */
    if (u1 === profil.bauch[1]) for (let i = Math.round(u1 / DU) + 1; i < n; i++) glatt[i] = g.a + g.b * i * DU;
  };
  ersetze(profil.hals);
  ersetze(profil.bauch);
  profil = profilZonen({ du: DU, rho: glatt, uBoden: vorlage.uBoden, gemessen: true, form: vorlage.form });
  /* Plausibel? Sonst Vorlage */
  if (!(profil.rHals > 0.15 && profil.rHals < 0.7 && profil.schulterU > profil.hals[0] + 0.4
    && profil.schulterU < profil.uBoden - 2)) return vorlage;
  return { ...profil, c: 1 };
}

function lueckenFuellen(roh) {
  const n = roh.length;
  const idx = [];
  for (let i = 0; i < n; i++) if (!Number.isNaN(roh[i])) idx.push(i);
  if (idx.length < n * 0.3) return null;
  const aus = new Float32Array(n);
  let j = 0;
  for (let i = 0; i < n; i++) {
    while (j < idx.length - 1 && idx[j + 1] <= i) j++;
    if (i <= idx[0]) aus[i] = roh[idx[0]];
    else if (i >= idx[idx.length - 1]) aus[i] = roh[idx[idx.length - 1]];
    else {
      const a = idx[j], b = idx[j + 1];
      aus[i] = roh[a] + (roh[b] - roh[a]) * (i - a) / (b - a);
    }
  }
  return aus;
}

function medianGlaetten(v, fenster) {
  const h = Math.floor(fenster / 2), aus = new Float32Array(v.length);
  for (let i = 0; i < v.length; i++) {
    const w = [];
    for (let k = Math.max(0, i - h); k <= Math.min(v.length - 1, i + h); k++) w.push(v[k]);
    aus[i] = median(w);
  }
  return aus;
}

function mittelGlaetten(v, fenster) {
  const h = Math.floor(fenster / 2), aus = new Float32Array(v.length);
  for (let i = 0; i < v.length; i++) {
    let s = 0, n = 0;
    for (let k = Math.max(0, i - h); k <= Math.min(v.length - 1, i + h); k++) { s += v[k]; n++; }
    aus[i] = s / n;
  }
  return aus;
}


/** Umrisslinie für die Anzeige: Punkte [x, y] im Bild, rechts hinunter
    und links hinauf. */
function umrissLinie(lage, profil, schritte = 60) {
  const rechts = [], links = [];
  for (let k = 0; k <= schritte; k++) {
    const u = profil.uBoden * k / schritte;
    const y = lage.oben + u * lage.S;
    const a = achseX(lage, y), r = rhoBei(profil, u) * lage.R;
    rechts.push([a + r, y]); links.push([a - r, y]);
  }
  return [...rechts, ...links.reverse()];
}



export {
  DU, achseX, lageFinden, lageSuchen, lageVerfolgen, luminanz, oberkanteSuchen, profilAusForm, profilBilden,
  profilSammeln, profilSammler, profilZonen, rhoBei, robusteGerade, schulterSuchen, umrissLinie, yBezug,
};
